import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMemo, useState } from 'react';
import PaymentMethodList from './payment-method-list';
import { SparkAdminPaymentMethodDto } from '../../../../../libs/contracts/src';
import { Upload } from '../upload';
import { Iconify } from '../iconify';

type PaymentMethodWithDetails = SparkAdminPaymentMethodDto & {
  id: number;
  requirePaymentScreenshot: boolean;
  description?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (
    paymentMethodId: number | null,
    paymentScreenshotFile: File | null,
  ) => void;
  paymentMethods: SparkAdminPaymentMethodDto[];
};

export default function PaymentMethodSelector({
  open,
  onClose,
  onSelect,
  paymentMethods,
}: Props) {
  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
  const [requirePaymentScreenshot, setRequirePaymentScreenshot] =
    useState(false);
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);

  const selectedMethod = useMemo(
    () =>
      (paymentMethods as PaymentMethodWithDetails[])?.find(
        (pm) => pm.id === selectedMethodId,
      ),
    [paymentMethods, selectedMethodId],
  );

  const handleMethodSelect = (
    methodId: number,
    requireScreenshot: boolean,
  ) => {
    setSelectedMethodId(methodId);
    setRequirePaymentScreenshot(requireScreenshot);
    setScreenshotFile(null);
  };

  const handleConfirm = () => {
    onSelect(selectedMethodId, requirePaymentScreenshot ? screenshotFile : null);
    onClose();
  };

  const isConfirmDisabled =
    !selectedMethodId || (requirePaymentScreenshot && !screenshotFile);

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Payments</DialogTitle>
      <DialogContent>
        <PaymentMethodList
          selectedMethodId={selectedMethodId}
          onSelect={handleMethodSelect}
          paymentMethods={paymentMethods as PaymentMethodWithDetails[]}
        />

        {requirePaymentScreenshot && selectedMethod?.description && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Iconify icon="solar:info-circle-bold" width={20} />
              <Typography variant="subtitle1">Usage Instructions</Typography>
            </Stack>
            <Typography variant="body2" component="pre">
              {selectedMethod.description}
            </Typography>
          </Stack>
        )}

        {requirePaymentScreenshot && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Iconify icon="solar:card-bold" width={20} />
              <Typography variant="subtitle1">Payment Screenshot</Typography>
            </Stack>
            <Upload
              value={screenshotFile}
              onDrop={(acceptedFiles) => {
                if (acceptedFiles.length > 0) {
                  setScreenshotFile(acceptedFiles[0]);
                }
              }}
              onDelete={() => setScreenshotFile(null)}
              accept={{ 'image/*': [] }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          size="large"
          fullWidth
          disabled={isConfirmDisabled}
          onClick={handleConfirm}
        >
          Continue
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
